import type { SupplierOrderStatusHistoryDto } from '../../dto/supplierOrder';
import type { StockValuation } from './stockValuation/stockValuationApi';

/**
 * „Historia” on the ZD card: status changes plus the ZD valuation revisions
 * (StockValuationRevision — e.g. „Faktura transportowa …: przeliczenie kursu
 * NBP”), newest first. Review of PR #418 on staging: the valuation history was
 * only in the API. The revisions come from GET /supplier-orders/:id/stock-valuation
 * (purchase-price access); without it the card shows statuses only.
 */
export type SupplierOrderHistoryItem =
  | { key: string; kind: 'status'; at: string | null; status: string; by: string | null; note: string | null }
  | { key: string; kind: 'valuation'; at: string | null; by: string | null; note: string };

function time(value: string | null): number {
  const parsed = value ? Date.parse(value) : NaN;
  return Number.isFinite(parsed) ? parsed : -Infinity;
}

export function mergeSupplierOrderHistory(
  statusEntries: SupplierOrderStatusHistoryDto[],
  valuationHistory: StockValuation['history'] | null | undefined,
): SupplierOrderHistoryItem[] {
  const items: SupplierOrderHistoryItem[] = [
    ...statusEntries.map((entry): SupplierOrderHistoryItem => ({
      key: `status-${entry.id}`,
      kind: 'status',
      at: entry.changedAt ?? null,
      status: entry.status,
      by: entry.changedByName ?? null,
      note: entry.notes ?? null,
    })),
    ...(valuationHistory || []).map((entry): SupplierOrderHistoryItem => ({
      key: `valuation-${entry.id}`,
      kind: 'valuation',
      at: entry.createdAt ?? null,
      by: entry.createdBy ?? null,
      note: entry.reason,
    })),
  ];
  // Stable: equal times keep the source order (statuses as sent, then valuation).
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => (time(b.item.at) - time(a.item.at)) || (a.index - b.index))
    .map(({ item }) => item);
}
